<template>
  <AppShell>
    <section v-if="!user" class="auth">
      <div class="auth-card">
        <div class="brand">
          <div class="logo">
            <img class="mark" src="/logo.png" alt="" width="36" height="36" draggable="false" />
            算力百科
          </div>
          <p class="tagline">全国首个算力和大模型工程服务</p>
          <ul>
            <li>按卡名、显存、FP16、FP8 查参数</li>
            <li>对照官方、市场和二手价格</li>
            <li>按需求选型，按 BOM 估价整机</li>
            <li>招标约束提取后按规则打分</li>
          </ul>
          <p class="note">邮箱注册。登录后 30 天内不用重复输入密码。关注公众号可领取积分。</p>
        </div>
        <div class="panel">
        <div v-if="mode !== 'reset'" class="tabs">
          <button type="button" :class="{ on: mode === 'login' }" @click="mode = 'login'">登录</button>
          <button type="button" :class="{ on: mode === 'register' }" @click="mode = 'register'">注册</button>
        </div>
        <div v-else class="reset-head">
          <button type="button" class="text-link" @click="mode = 'login'">返回登录</button>
          <h2>找回密码</h2>
          <p class="muted">验证码会发到注册邮箱，通过后即可设置新密码。</p>
        </div>
        <label>邮箱 <input v-model="email" type="email" autocomplete="username" /></label>
        <label>
          {{ mode === 'reset' ? '新密码' : '密码' }}
          <input
            v-model="password"
            type="password"
            :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
          />
        </label>
        <label v-if="mode === 'register' || mode === 'reset'">
          邮箱验证码
          <span class="code-row">
            <input v-model="code" maxlength="6" inputmode="numeric" placeholder="6 位验证码" />
            <button type="button" class="ghost" :disabled="sendingCode || codeWait > 0" @click="sendCode">
              {{ codeWait > 0 ? `${codeWait}s` : (sendingCode ? '发送中…' : '获取验证码') }}
            </button>
          </span>
        </label>
        <button type="button" class="primary" :disabled="submitting" @click="submit">
          {{ submitLabel }}
        </button>
        <p v-if="mode === 'login'" class="forgot">
          <button type="button" class="text-link" @click="mode = 'reset'">忘记密码</button>
        </p>
        <p v-if="error" class="error">{{ error }}</p>
        </div>
      </div>
    </section>

    <template v-else>
    <h1>账户</h1>
    <section class="panel wide">
      <div class="head">
        <div>
          <p class="email">{{ user.email }}</p>
          <p v-if="balances" class="bal">积分 {{ balances.points }} · Token {{ balances.tokens }}</p>
          <p class="muted" style="margin:6px 0 0">
            微信：{{ user.wechat_bound ? '已绑定' : '未绑定' }}
          </p>
        </div>
        <div class="head-actions">
          <button type="button" class="primary" :disabled="openingMaas" @click="openMaas">
            {{ openingMaas ? '跳转中…' : '进入 MaaS 控制台' }}
          </button>
          <button type="button" class="ghost" @click="logout">退出登录</button>
        </div>
      </div>
      <p v-if="maasError" class="error">{{ maasError }}</p>

      <div class="help">
        <p class="muted">
          积分不够时，点右上角「关注送积分」。同一邮箱账号可进入 MaaS 网关控制台。
        </p>
        <div class="help-card">
          <strong>联系运营</strong>
          <img
            v-if="qrVisible"
            src="/images/qrcode.png"
            alt="联系运营"
            @error="qrVisible = false"
          />
        </div>
      </div>

      <h3>任务</h3>
      <ul v-if="tasks.length" class="tasks">
        <li v-for="item in tasks" :key="item.key">
          <router-link :to="item.to">{{ item.label }}</router-link>
          <span>{{ item.status }}</span>
          <span v-if="item.time">{{ item.time }}</span>
        </li>
      </ul>
      <p v-else class="muted">招标和选型完成后会出现在这里。</p>

      <h3>最近流水</h3>
      <table v-if="ledger.length">
        <thead>
          <tr><th>时间</th><th>动作</th><th>变动</th><th>余额</th></tr>
        </thead>
        <tbody>
          <tr v-for="row in ledger" :key="row.id">
            <td>{{ formatTime(row.created_at) }}</td>
            <td>{{ actionLabel(row.action) }}</td>
            <td :class="{ plus: row.delta > 0, minus: row.delta < 0 }">{{ row.delta }}</td>
            <td>{{ row.balance_after }}</td>
          </tr>
        </tbody>
      </table>
        <p v-else class="muted">暂无流水</p>
    </section>
    </template>
  </AppShell>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import { clearSession, setSession } from '../utils/session'
import {
  billingLedger,
  fetchMe,
  listAdviseJobs,
  listBidJobs,
  loginUser,
  openMaasConsole,
  registerUser,
  resetPassword,
  sendEmailCode,
  sendResetCode
} from '../api/platformService'
import { notifyAuthChanged } from '../utils/authBus'
import { actionLabel as ledgerActionLabel, formatLedgerTime } from '../utils/ledgerLabels'

export default {
  name: 'AccountWorkspace',
  components: { AppShell },
  data() {
    return {
      mode: 'login',
      email: '',
      password: '',
      code: '',
      user: null,
      balances: null,
      ledger: [],
      tasks: [],
      error: '',
      submitting: false,
      sendingCode: false,
      codeWait: 0,
      codeTimer: null,
      qrVisible: true,
      openingMaas: false,
      maasError: ''
    }
  },
  computed: {
    submitLabel() {
      if (this.submitting) return '提交中…'
      if (this.mode === 'register') return '注册并领取积分'
      if (this.mode === 'reset') return '重置密码并登录'
      return '登录'
    }
  },
  async mounted() {
    if (this.$route.query.mode === 'register') this.mode = 'register'
    if (this.$route.query.mode === 'login') this.mode = 'login'
    await this.refresh()
  },
  methods: {
    actionLabel(action) {
      return ledgerActionLabel(action)
    },
    formatTime(iso) {
      return formatLedgerTime(iso)
    },
    async refresh() {
      try {
        const me = await fetchMe()
        if (me.token && me.role) setSession(me.token, me.role)
        this.user = me.user
        this.balances = me.balances || null
        if (me.user) {
          this.ledger = await billingLedger()
          await this.loadTasks()
        } else {
          this.user = null
          this.tasks = []
        }
      } catch (e) {
        this.user = null
      }
    },
    async openMaas() {
      this.maasError = ''
      this.openingMaas = true
      try {
        const result = await openMaasConsole()
        if (!result?.url) {
          this.maasError = '未配置 MaaS 控制台地址'
          return
        }
        window.location.href = result.url
      } catch (err) {
        this.maasError = err.response?.data?.error || '无法打开 MaaS 控制台'
      } finally {
        this.openingMaas = false
      }
    },
    async submit() {
      this.error = ''
      this.submitting = true
      try {
        if (this.mode === 'register' || this.mode === 'reset') {
          if (!this.code.trim()) {
            this.error = '请填写邮箱验证码'
            return
          }
          const payload = {
            email: this.email,
            password: this.password,
            code: this.code
          }
          const result = this.mode === 'reset'
            ? await resetPassword(payload)
            : await registerUser(payload)
          setSession(result.token, result.role)
          this.user = result.user
          this.balances = result.balances
        } else {
          const result = await loginUser({
            email: this.email,
            password: this.password
          })
          setSession(result.token, result.role)
          this.user = result.user
          this.balances = result.balances
        }
        this.ledger = await billingLedger()
        await this.loadTasks()
        notifyAuthChanged()
      } catch (err) {
        this.error = err.response?.data?.error || '失败'
      } finally {
        this.submitting = false
      }
    },
    startCodeWait(seconds) {
      this.codeWait = seconds
      if (this.codeTimer) clearInterval(this.codeTimer)
      this.codeTimer = setInterval(() => {
        this.codeWait -= 1
        if (this.codeWait <= 0) {
          this.codeWait = 0
          clearInterval(this.codeTimer)
          this.codeTimer = null
        }
      }, 1000)
    },
    async sendCode() {
      this.error = ''
      if (!this.email.trim()) {
        this.error = '请先填写邮箱'
        return
      }
      this.sendingCode = true
      try {
        const send = this.mode === 'reset' ? sendResetCode : sendEmailCode
        const data = await send({ email: this.email.trim() })
        this.startCodeWait(data.seconds || 60)
      } catch (err) {
        this.error = err.response?.data?.error || '验证码发送失败'
      } finally {
        this.sendingCode = false
      }
    },
    async loadTasks() {
      const statusText = { pending: '排队中', running: '进行中', done: '已完成', failed: '失败' }
      try {
        const [bids, advises] = await Promise.all([listBidJobs(), listAdviseJobs()])
        const bidItems = (bids.jobs || []).map((job) => ({
          key: `bid-${job.id}`,
          label: `招标 #${job.id} ${job.source_filename || ''}`.trim(),
          status: statusText[job.status] || job.status,
          time: this.formatTime(job.created_at),
          to: { path: '/bid', query: { job: String(job.id) } }
        }))
        const adviseItems = (advises.jobs || []).map((job) => ({
          key: `advise-${job.id}`,
          label: `选型 ${ (job.text || '').slice(0, 24) }`,
          status: statusText[job.status] || job.status,
          time: this.formatTime(job.created_at),
          to: { path: '/advise', query: { job: String(job.id) } }
        }))
        this.tasks = [...adviseItems, ...bidItems].slice(0, 12)
      } catch (e) {
        this.tasks = []
      }
    },
    logout() {
      clearSession()
      this.user = null
      this.balances = null
      this.ledger = []
      this.tasks = []
      notifyAuthChanged()
    }
  },
  beforeUnmount() {
    if (this.codeTimer) clearInterval(this.codeTimer)
  }
}
</script>

<style scoped>
h1 { font-size: 34px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.auth {
  min-height: 520px;
  margin: -8px -8px 0;
  padding: 28px 8px 12px;
  border-radius: 24px;
  background:
    radial-gradient(420px 220px at 12% 0%, rgba(22, 93, 255, 0.16), transparent 70%),
    radial-gradient(360px 240px at 88% 100%, rgba(22, 93, 255, 0.08), transparent 70%),
    #f2f5fb;
}
.auth-card {
  max-width: 860px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(29, 33, 41, 0.06);
}
.brand {
  padding: 36px 32px;
  background:
    linear-gradient(160deg, #165dff 0%, #0e42d2 100%);
  color: #fff;
}
.logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.mark {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.12);
}
.tagline { margin: 16px 0 18px; font-size: 15px; line-height: 1.6; color: rgba(255, 255, 255, 0.92); }
.brand ul { margin: 0; padding: 0; list-style: none; display: grid; gap: 10px; }
.brand li {
  position: relative;
  padding-left: 16px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.88);
}
.brand li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
}
.note { margin: 22px 0 0; font-size: 13px; line-height: 1.6; color: rgba(255, 255, 255, 0.72); }
.auth-card .panel { border: 0; border-radius: 0; padding: 32px 28px; box-shadow: none; }
.grid { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 16px; }
.grid.single { grid-template-columns: minmax(0, 480px); }
.panel {
  background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 16px;
}
.panel.wide { max-width: 720px; }
.tabs { display: flex; gap: 8px; margin-bottom: 12px; }
.tabs button {
  border: 1px solid #e5e6eb; background: #fff; color: #1d2129; border-radius: 12px;
  padding: 6px 12px; cursor: pointer;
}
.tabs button.on { background: #165dff; color: #fff; border-color: #165dff; }
.reset-head h2 { margin: 8px 0 4px; font-size: 20px; }
.forgot { margin: 10px 0 0; }
.text-link {
  border: 0; background: transparent; padding: 0; color: #165dff; cursor: pointer; font-size: 13px;
}
label { display: grid; gap: 6px; margin-bottom: 10px; font-size: 14px; }
.optional { color: #86909c; }
.code-row { display: flex; gap: 8px; }
.code-row input { flex: 1; min-width: 0; }
.code-row .ghost { white-space: nowrap; }
input {
  border: 1px solid #e5e6eb; border-radius: 12px; padding: 8px 10px; font: inherit; color: #1d2129;
}
.primary {
  border: 0; background: #165dff; color: #fff; border-radius: 12px; padding: 10px 14px; cursor: pointer;
}
.primary:disabled { opacity: 0.6; cursor: not-allowed; }
.ghost {
  border: 1px solid #e5e6eb; background: #fff; color: #4e5969; border-radius: 12px; padding: 8px 12px; cursor: pointer;
}
.side ol { margin: 0 0 12px 18px; color: #4e5969; font-size: 14px; }
.side img { width: 180px; border-radius: 16px; background: #fff; }
.head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 12px; }
.head-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.email { font-weight: 600; margin: 0; }
.bal { color: #165dff; margin: 4px 0 0; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th, td { border-top: 1px solid #e5e6eb; padding: 8px 6px; text-align: left; }
.plus { color: #188038; }
.minus { color: #d3382c; }
.error { color: #d3382c; margin-top: 8px; }
.ok { color: #188038; }
.muted { color: #86909c; font-size: 13px; margin-bottom: 12px; line-height: 1.6; }
.tasks { list-style: none; margin: 0 0 18px; padding: 0; display: grid; gap: 8px; }
.tasks li {
  display: flex; gap: 10px; align-items: baseline; flex-wrap: wrap;
  background: #f7f8fa; border-radius: 12px; padding: 8px 12px; font-size: 13px;
}
.tasks a { color: #165dff; text-decoration: none; }
.bind { margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #e5e6eb; }
.bind h3 { margin: 0 0 6px; font-size: 15px; }
.bind-row { display: flex; gap: 8px; }
.bind-row input { flex: 1; }
.help { margin-bottom: 18px; }
.help-card {
  display: flex; justify-content: space-between; gap: 16px; align-items: center;
  background: #f7f8fa; border: 1px solid #e5e6eb; border-radius: 16px; padding: 12px 14px;
}
.help-card p { margin: 6px 0 0; color: #4e5969; font-size: 13px; line-height: 1.5; }
.help-card img { width: 160px; height: 160px; object-fit: contain; border-radius: 6px; background: #fff; }
@media (max-width: 800px) {
  .grid { grid-template-columns: 1fr; }
  .auth-card { grid-template-columns: 1fr; }
  .brand { padding: 24px 20px; }
  .help-card { flex-direction: column; align-items: flex-start; }
}
</style>
